/**
 * Offline Data Cache Utility
 * Caches customer and job data in localStorage for offline access
 */

const CACHE_PREFIX = 'streakfree_cache_';
const CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

// Cache keys
const CACHE_KEYS = {
  CUSTOMERS: 'customers',
  JOBS: 'jobs',
  ROUNDS: 'rounds',
  USER: 'user',
  LAST_SYNC: 'last_sync'
};

/**
 * Save data to cache with timestamp
 */
export const saveToCache = (key, data) => {
  try {
    const cacheData = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(`${CACHE_PREFIX}${key}`, JSON.stringify(cacheData));
    console.log(`[Cache] Saved ${key} to cache`);
  } catch (err) {
    console.error(`[Cache] Error saving ${key}:`, err);
    // If storage is full, try clearing old data
    if (err.name === 'QuotaExceededError') {
      clearExpiredCache();
    }
  }
};

/**
 * Get data from cache (returns null if expired or not found)
 */
export const getFromCache = (key, ignoreExpiry = false) => {
  try {
    const cached = localStorage.getItem(`${CACHE_PREFIX}${key}`);
    if (!cached) return null;
    
    const { data, timestamp } = JSON.parse(cached);
    
    // Check if expired (unless ignoreExpiry for offline mode)
    if (!ignoreExpiry && Date.now() - timestamp > CACHE_EXPIRY_MS) {
      console.log(`[Cache] ${key} cache expired`);
      return null;
    }
    
    return data;
  } catch (err) {
    console.error(`[Cache] Error reading ${key}:`, err);
    return null;
  }
};

/**
 * Clear specific cache key
 */
export const clearCache = (key) => {
  try {
    localStorage.removeItem(`${CACHE_PREFIX}${key}`);
  } catch (err) {
    console.error(`[Cache] Error clearing ${key}:`, err);
  }
};

/**
 * Clear all expired cache entries
 */
export const clearExpiredCache = () => {
  try {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(CACHE_PREFIX));
    keys.forEach(key => {
      const cached = localStorage.getItem(key);
      if (cached) {
        try {
          const { timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp > CACHE_EXPIRY_MS) {
            localStorage.removeItem(key);
            console.log(`[Cache] Cleared expired: ${key}`);
          }
        } catch {
          // Invalid JSON, remove it
          localStorage.removeItem(key);
        }
      }
    });
  } catch (err) {
    console.error('[Cache] Error clearing expired cache:', err);
  }
};

/**
 * Clear all cache
 */
export const clearAllCache = () => {
  try {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(CACHE_PREFIX));
    keys.forEach(key => localStorage.removeItem(key));
    console.log('[Cache] Cleared all cache');
  } catch (err) {
    console.error('[Cache] Error clearing all cache:', err);
  }
};

/**
 * Check if we're currently online
 */
export const isOnline = () => navigator.onLine;

/**
 * Wrapper for fetch that caches results and returns cached data when offline
 */
export const cachedFetch = async (url, options = {}, cacheKey = null) => {
  const effectiveCacheKey = cacheKey || url;
  
  // If online, try to fetch fresh data
  if (isOnline()) {
    try {
      const response = await fetch(url, options);
      if (response.ok) {
        const data = await response.json();
        // Cache the successful response
        if (effectiveCacheKey) {
          saveToCache(effectiveCacheKey, data);
        }
        return { data, fromCache: false, error: null };
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (err) {
      console.error(`[Cache] Fetch failed for ${url}:`, err);
      // Try to return cached data if fetch fails
      const cached = getFromCache(effectiveCacheKey, true);
      if (cached) {
        return { data: cached, fromCache: true, error: err };
      }
      return { data: null, fromCache: false, error: err };
    }
  } else {
    // Offline - return cached data
    console.log(`[Cache] Offline, using cached data for ${effectiveCacheKey}`);
    const cached = getFromCache(effectiveCacheKey, true);
    if (cached) {
      return { data: cached, fromCache: true, error: null };
    }
    return { data: null, fromCache: true, error: new Error('Offline and no cached data available') };
  }
};

/**
 * Save customer data to cache
 */
export const cacheCustomers = (customers) => {
  saveToCache(CACHE_KEYS.CUSTOMERS, customers);
};

/**
 * Get cached customers (for offline use)
 */
export const getCachedCustomers = () => {
  return getFromCache(CACHE_KEYS.CUSTOMERS, true) || [];
};

/**
 * Save jobs data to cache
 */
export const cacheJobs = (jobs) => {
  saveToCache(CACHE_KEYS.JOBS, jobs);
};

/**
 * Get cached jobs (for offline use)
 */
export const getCachedJobs = () => {
  return getFromCache(CACHE_KEYS.JOBS, true) || [];
};

/**
 * Save user data to cache
 */
export const cacheUser = (user) => {
  saveToCache(CACHE_KEYS.USER, user);
};

/**
 * Get cached user
 */
export const getCachedUser = () => {
  return getFromCache(CACHE_KEYS.USER, true);
};

/**
 * Update last sync timestamp
 */
export const updateLastSync = () => {
  saveToCache(CACHE_KEYS.LAST_SYNC, Date.now());
};

/**
 * Get last sync timestamp
 */
export const getLastSync = () => {
  return getFromCache(CACHE_KEYS.LAST_SYNC, true);
};

export { CACHE_KEYS };
